import { useState } from "react";

import ModalCloseIcon from "@/components/icons/modal-close-icon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import Filters from "../../filters";
import useSelectedCategories from "../../hooks/use-selected-categories";

export interface FiltersModalProps extends React.ComponentProps<typeof Dialog> {
  onClear(): void;
  onSubmit(form: FormData): void;
}

export default function FiltersModal({
  onClear,
  onSubmit,
  ...props
}: FiltersModalProps) {
  const [changed, setChanged] = useState(false);

  const { includeLabel } = useSelectedCategories();

  const handleClose = () => props.onOpenChange?.(false);

  const handleClear = () => {
    onClear();
    handleClose();
  };

  const handleFormChange = () => setChanged(true);

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(new FormData(event.currentTarget));
    handleClose();
  };

  return (
    <Dialog {...props}>
      <DialogContent className="h-full overflow-y-auto rounded-none bg-[url(/images/body.png)] bg-midnight-express bg-size-[300%] bg-top md:bg-size-[100%]">
        <form
          autoComplete="off"
          autoCorrect="off"
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          spellCheck="false"
        >
          <DialogHeader className="relative flex h-[65px] items-center justify-center border-white/10 border-b py-0">
            <Button
              aria-label="Close"
              className="absolute top-1/2 left-6 -mt-[13px] flex size-[25px] items-center justify-center rounded-full border border-white"
              onClick={handleClose}
              type="button"
            >
              <ModalCloseIcon />
            </Button>
            <DialogTitle className="font-semibold text-16 text-white">
              Filter {includeLabel.toLocaleLowerCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pt-5 pb-6">
            <Filters onChange={handleFormChange} />
          </div>
          <DialogFooter className="sticky bottom-0 flex h-[85px] w-full flex-row items-center justify-between border-white/10 border-t bg-space-gray-dark px-6 py-0">
            <Button
              className="font-medium text-14 text-white underline"
              onClick={handleClear}
              type="button"
            >
              Clear all
            </Button>
            <Button
              className={cn(
                "h-11 w-[141px] rounded-[22px] transition-opacity",
                changed ? "opacity-100" : "opacity-0"
              )}
              type="submit"
              variant="cta"
            >
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
